<template>
  <div>
    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Carregando..." />
    </div>

    <template v-else>
      <!-- Cabeçalho -->
      <b-card>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h3 class="mb-0">Pré-Entrevista por IA</h3>
            <small class="text-muted" v-if="customerName">{{ customerName }}</small>
          </div>
          <div class="text-right">
            <b-badge :variant="statusData.variant">{{ statusData.label }}</b-badge>
            <div class="text-muted mt-50" v-if="results && results.completed_at">
              Concluída em {{ results.completed_at | dateTime }}
            </div>
          </div>
        </div>
      </b-card>

      <!-- Ainda não concluída -->
      <b-card v-if="!isCompleted">
        <div class="text-center py-3">
          <feather-icon icon="ClockIcon" size="32" class="text-muted mb-1" />
          <p class="mb-0">
            A Pré-Entrevista por IA ainda não foi concluída pelo cliente.
          </p>
          <p class="text-muted">Status atual: {{ statusData.label }}</p>
        </div>
      </b-card>

      <template v-else>
        <!-- Resumo executivo -->
        <b-card title="Resumo executivo" v-if="results.ai_summary">
          <p class="mb-0" style="white-space: pre-line">{{ results.ai_summary }}</p>
          <small class="text-muted d-block mt-1" v-if="results.engine_version">
            Versão do modelo: {{ results.engine_version }}
          </small>
        </b-card>

        <!-- Pontos de atenção -->
        <b-card
          title="Pontos de atenção"
          v-if="results.points_of_attention && results.points_of_attention.length"
        >
          <ul class="mb-0">
            <li v-for="(point, idx) in results.points_of_attention" :key="idx">
              {{ typeof point === "string" ? point : point.text || point.title }}
            </li>
          </ul>
        </b-card>

        <!-- Preparação do PN -->
        <b-card title="Preparação para a entrevista" v-if="results.pn_prep_info">
          <ai-answer-tree :value="results.pn_prep_info" />
        </b-card>

        <!-- Divergências com o cadastro -->
        <b-card
          title="Comparar com o cadastro"
          v-if="divergences && divergences.length"
        >
          <p class="text-muted">
            Estes dados <strong>não alteram o cadastro automaticamente</strong>.
            Revise as divergências e escolha o que deseja aplicar.
          </p>
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th style="width: 40px" v-if="canApplyToCadastro"></th>
                  <th>Campo</th>
                  <th>Cadastro atual</th>
                  <th>Resposta da IA</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="d in divergences"
                  :key="d.field"
                  :class="{ 'diverges-row': d.diverges }"
                >
                  <td v-if="canApplyToCadastro">
                    <b-form-checkbox
                      v-model="selectedFields"
                      :value="d.field"
                      :disabled="!d.diverges"
                    />
                  </td>
                  <td class="text-capitalize">{{ humanize(d.field) }}</td>
                  <td>{{ formatFieldValue(d.field, d.current) }}</td>
                  <td>{{ formatFieldValue(d.field, d.incoming) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-button
            v-if="canApplyToCadastro"
            variant="primary"
            :disabled="!selectedFields.length || promoting"
            @click="applyToCadastro"
          >
            <b-spinner small v-if="promoting" /> Aplicar ao cadastro
          </b-button>
        </b-card>

        <!-- Respostas completas -->
        <b-card title="Respostas completas" v-if="hasStructuredData">
          <div
            v-for="section in structuredSections"
            :key="section"
            class="mb-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-50">
              <h5 class="mb-0">{{ humanize(section) }}</h5>
              <b-button
                v-if="canApplyToCadastro && section !== 'customers'"
                variant="outline-primary"
                size="sm"
                :disabled="applyingSection === section"
                @click="applySection(section)"
              >
                <b-spinner small v-if="applyingSection === section" />
                Aplicar à entrevista
              </b-button>
            </div>
            <ai-answer-tree :value="results.structured_data[section]" />
            <hr />
          </div>
          <small class="text-muted" v-if="canApplyToCadastro">
            "Aplicar à entrevista" grava as respostas desta seção na entrevista do
            cliente. Os dados pessoais são aplicados ao cadastro na seção acima.
          </small>
        </b-card>
      </template>
    </template>
  </div>
</template>

<script>
import {
  BCard,
  BBadge,
  BButton,
  BSpinner,
  BFormCheckbox,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import AiAnswerTree from "../components/AiAnswerTree.vue";
import * as types from "../store/types";
import { UPDATE_ACTION, CUSTOMER_RESOURCE } from "@/constants/resources";
import { aiFieldLabel, aiFormatValue } from "../constants/aiPreInterviewLabels";

const AI_STATUS_MAP = {
  nao_enviada: { label: "Não enviada", variant: "secondary" },
  enviada: { label: "Enviada", variant: "info" },
  acessada: { label: "Acessada", variant: "primary" },
  em_preenchimento: { label: "Em preenchimento", variant: "warning" },
  concluida: { label: "Concluída", variant: "success" },
  expirada: { label: "Expirada", variant: "dark" },
  erro: { label: "Erro", variant: "danger" },
  reaberta: { label: "Reaberta", variant: "info" },
};

export default {
  components: {
    BCard,
    BBadge,
    BButton,
    BSpinner,
    BFormCheckbox,
    AiAnswerTree,
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      loading: true,
      promoting: false,
      applyingSection: null,
      customerToken: null,
      customerName: null,
      results: null,
      selectedFields: [],
    };
  },
  computed: {
    statusData() {
      const status = this.results?.status || "nao_enviada";
      return AI_STATUS_MAP[status] || AI_STATUS_MAP.nao_enviada;
    },
    isCompleted() {
      // Há resultados a mostrar (inclui o estado "reaberta", que preserva os dados).
      return !!(this.results && this.results.completed_at);
    },
    divergences() {
      return this.results?.divergences || [];
    },
    hasStructuredData() {
      return (
        this.results?.structured_data &&
        Object.keys(this.results.structured_data).length > 0
      );
    },
    structuredSections() {
      return this.hasStructuredData
        ? Object.keys(this.results.structured_data)
        : [];
    },
    canApplyToCadastro() {
      return this.$can(UPDATE_ACTION, CUSTOMER_RESOURCE);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions({
      getCustomer: types.GET_CUSTOMER,
      getResults: types.GET_AI_PRE_INTERVIEW_RESULTS,
      promote: types.PROMOTE_AI_PRE_INTERVIEW,
    }),
    load() {
      this.loading = true;
      this.getCustomer(this.$route.params.id)
        .then((resp) => {
          this.customerToken = resp.data?.token;
          this.customerName = resp.data?.name;
          return this.getResults(this.customerToken);
        })
        .then((resp) => {
          this.results = resp.data;
        })
        .catch(() => {
          this.notify(
            "Oops!",
            "Não foi possível carregar a Pré-Entrevista por IA.",
            "danger"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyToCadastro() {
      this.promoting = true;
      this.promote({
        customerToken: this.customerToken,
        sections: ["customers"],
        personal_fields: this.selectedFields,
      })
        .then((resp) => {
          // o backend responde 200 com o status por seção — um erro (ex.: e-mail
          // já usado por outro cliente) chega aqui, não no catch
          const result = resp.data && resp.data.result && resp.data.result.customers;
          if (result && result.status === "ok") {
            this.notify(
              "Cadastro atualizado!",
              "Os campos selecionados foram aplicados ao cadastro do cliente.",
              "success"
            );
            this.selectedFields = [];
            this.load();
          } else {
            this.notify(
              "Não aplicado",
              (result && result.message) ||
                "Não foi possível aplicar os dados ao cadastro.",
              "warning"
            );
          }
        })
        .catch(() => {
          this.notify(
            "Oops!",
            "Não foi possível aplicar os dados ao cadastro.",
            "danger"
          );
        })
        .finally(() => {
          this.promoting = false;
        });
    },
    applySection(section) {
      this.applyingSection = section;
      this.promote({
        customerToken: this.customerToken,
        sections: [section],
      })
        .then((resp) => {
          const result = resp.data && resp.data.result && resp.data.result[section];
          if (result && result.status === "ok") {
            this.notify(
              "Aplicado à entrevista!",
              "As respostas de " +
                this.humanize(section) +
                " foram aplicadas à entrevista do cliente.",
              "success"
            );
          } else {
            this.notify(
              "Não aplicado",
              (result && result.message) ||
                "Não foi possível aplicar esta seção à entrevista.",
              "warning"
            );
          }
        })
        .catch(() => {
          this.notify(
            "Oops!",
            "Não foi possível aplicar à entrevista. Tente novamente.",
            "danger"
          );
        })
        .finally(() => {
          this.applyingSection = null;
        });
    },
    humanize(k) {
      return aiFieldLabel(k);
    },
    formatFieldValue(field, value) {
      return aiFormatValue(field, value);
    },
    notify(title, text, variant) {
      this.toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: variant === "danger" ? "AlertTriangleIcon" : "CheckCircleIcon",
          variant,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// Destaque de divergência neutro aos temas: tinta translúcida do warning do
// tema (funciona sobre fundo claro e escuro; o table-warning do Bootstrap
// pintava um amarelo opaco que destoava no dark mode).
.diverges-row td {
  background-color: rgba(255, 159, 67, 0.14);
}
</style>
